import { z } from "zod"

import { agentKinds } from "../../agents/vocabulary.js"
import { selectableDocumentTypeKeys } from "../../registries.js"
import { documentDerivedStates, documentSortKeys } from "../../taxonomy.js"
import { ApiJsonObjectSchema, ApiPaginationQuerySchema } from "./common.js"
import { ApiUserSummarySchema } from "./users.js"

export const ApiDocumentListQuerySchema = ApiPaginationQuerySchema.extend({
  state: z.enum(documentDerivedStates).optional(),
  search: z.string().trim().min(1).max(200).optional(),
  sort: z.enum(documentSortKeys).optional(),
  documentTypeKey: z.enum(selectableDocumentTypeKeys).optional(),
  // Narrows to the documents this agent kind is holding right now. An axis of
  // its own, not a lifecycle state: a held document keeps whatever state it
  // already had.
  agentHeld: z.enum(agentKinds).optional(),
  // "false" leaves out documents a client sent through the portal, which the
  // client's Files tab already lists as portal files; "true" keeps only those.
  fromPortal: z.enum(["true", "false"]).optional(),
})

export type ApiDocumentListQuery = z.infer<typeof ApiDocumentListQuerySchema>

export const ApiDocumentSummarySchema = z.object({
  documentId: z.string().uuid(),
  counterpartyName: z.string().nullable(),
  accountName: z.string().nullable(),
  accountNumberLast4: z.string().nullable(),
  documentNumber: z.string().nullable(),
  documentDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  currency: z.string().nullable(),
  subtotal: z.string().nullable(),
  taxTotal: z.string().nullable(),
  total: z.string().nullable(),
  recordCount: z.number().int(),
  confidence: z.number().nullable(),
  summary: ApiJsonObjectSchema,
  projectionHash: z.string(),
})

export type ApiDocumentSummary = z.infer<typeof ApiDocumentSummarySchema>

// Where the document came from, so the row's subtitle can say "Uploaded by
// Sean Lawton" or "Forwarded by sam@acme.com" instead of an internal id.
export const ApiDocumentOriginSchema = z.object({
  source: z.string(),
  actorUserId: z.string().nullable(),
  // Only the email path has a sender that is not the batch's creator.
  actorEmail: z.string().nullable(),
  actor: ApiUserSummarySchema.nullable(),
})

export type ApiDocumentOrigin = z.infer<typeof ApiDocumentOriginSchema>

export const ApiDocumentListItemSchema = z.object({
  documentId: z.string().uuid(),
  batchId: z.string().uuid(),
  title: z.string().nullable(),
  derivedState: z.enum(documentDerivedStates),
  externalReference: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  summary: ApiDocumentSummarySchema.nullable(),
  openFlagCount: z.number().int().nonnegative(),
  // Optional so older API deployments still parse.
  origin: ApiDocumentOriginSchema.nullable().optional(),
})

export type ApiDocumentListItem = z.infer<typeof ApiDocumentListItemSchema>

export const ApiDocumentListResponseSchema = z.object({
  documents: z.array(ApiDocumentListItemSchema),
  pagination: z.object({
    limit: z.number().int(),
    offset: z.number().int(),
    returned: z.number().int(),
    total: z.number().int().nonnegative(),
  }),
  // Document counts per derived state across the full filtered set (ignoring the
  // state filter), so the inbox lifecycle tabs can show accurate totals.
  stateCounts: z.record(z.string(), z.number().int().nonnegative()),
  // Live documents the statements agent is currently holding. Its own number
  // because a hold is not a derived state: a statement held for a workspace
  // reason (a new account, an overlapping period, a duplicate) carries no flag
  // and stays in `ready`, so `stateCounts` cannot see it.
  agentHeldCount: z.number().int().nonnegative(),
  // Everything waiting on a person, counted once: documents with an open
  // required flag, plus the ones the agent is holding that those do not
  // already cover. The sidebar badge reads this rather than adding two
  // overlapping numbers itself.
  needsAttentionCount: z.number().int().nonnegative(),
})

export type ApiDocumentListResponse = z.infer<
  typeof ApiDocumentListResponseSchema
>

export const ApiBatchStatusResponseSchema = z.object({
  batchId: z.string().uuid(),
  source: z.string(),
  idempotencyKey: z.string().nullable(),
  externalReference: z.string().nullable(),
  createdAt: z.string().datetime(),
  sealedAt: z.string().datetime(),
  uploadCount: z.number().int().nonnegative(),
  documentCount: z.number().int().nonnegative(),
  documentCounts: z.record(z.string(), z.number().int().nonnegative()),
  processingRunCounts: z.record(z.string(), z.number().int().nonnegative()),
})

export type ApiBatchStatusResponse = z.infer<
  typeof ApiBatchStatusResponseSchema
>

export const ApiArtifactPointerSchema = z.object({
  artifactId: z.string().uuid(),
  kind: z.string(),
  container: z.string(),
  blobPath: z.string(),
  contentHash: z.string(),
  byteSize: z.number().int().nonnegative(),
  mimeType: z.string(),
  retentionState: z.string(),
  expiresAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
})

export type ApiArtifactPointer = z.infer<typeof ApiArtifactPointerSchema>

// TODO(LED-46-placeholder): Documents v2 detail header asks for document type,
// document type version, processor version, page count, file size, and per-page
// progress. Only `artifacts[].byteSize` (file size) and
// `latestProcessingRun.state` are available today; the rest render as `—`
// placeholders in the frontend until the projection grows these fields.
export const ApiDocumentApprovalSchema = z.object({
  approvalId: z.string().uuid(),
  projectionHash: z.string(),
  /** Null exactly when an agent approved; `agentKind` names it instead. */
  approvedByUserId: z.string().nullable(),
  approvedBy: ApiUserSummarySchema.nullable(),
  /** The agent kind that approved, e.g. "bills"; null for a person. The
   * vocabulary itself, not a loose string: the column is that enum, and a
   * client that cannot narrow this cannot name the agent on the screen. */
  agentKind: z.enum(agentKinds).nullable().optional(),
  approvedAt: z.string().datetime(),
})

export type ApiDocumentApproval = z.infer<typeof ApiDocumentApprovalSchema>

/**
 * Whether the rows add up, named once. Derivable from the summary extensions
 * and the review flags, but only by reassembling it in every surface that
 * asks; naming it here is what lets the detail verdict and, later, a checked
 * mark in the list read the same answer.
 */
export const ApiDocumentReconciliationSchema = z.object({
  status: z.enum(["balanced", "off", "unknown"]),
  /** Set only when status is "unknown". */
  reason: z.enum(["not_applicable", "missing_figures"]).nullable(),
  currency: z.string().nullable(),
  rowCount: z.number().int().nonnegative(),
  openingBalance: z.string().nullable(),
  moneyIn: z.string().nullable(),
  moneyOut: z.string().nullable(),
  /** What the statement itself prints. */
  closingBalance: z.string().nullable(),
  /** Opening plus money in minus money out; reversed for a liability. */
  calculatedClosingBalance: z.string().nullable(),
  /** "liability" for a card or loan statement: payments lower the balance.
   * Optional so a web build newer than the API still parses. */
  balanceDirection: z.enum(["asset", "liability"]).nullable().optional(),
  /** Calculated minus printed, signed. Negative means the rows fall short. */
  difference: z.string().nullable(),
  /** Open amount_mismatch flags backing an "off" verdict. */
  flagIds: z.array(z.string().uuid()),
})

export type ApiDocumentReconciliation = z.infer<
  typeof ApiDocumentReconciliationSchema
>

export const ApiDocumentDetailResponseSchema = z.object({
  document: ApiDocumentListItemSchema,
  // Optional so a web build newer than the API still parses; absent means
  // "this deployment cannot answer", which the verdict renders as unknown.
  reconciliation: ApiDocumentReconciliationSchema.nullable().optional(),
  // The active approval, so the header can say who approved and when.
  approval: ApiDocumentApprovalSchema.nullable().optional(),
  // Pages the last posted processing charge billed for this document; null
  // when nothing has been posted yet.
  pageCount: z.number().int().nonnegative().nullable().optional(),
  artifacts: z.array(ApiArtifactPointerSchema),
  latestProcessingRun: z
    .object({
      processingRunId: z.string().uuid(),
      state: z.string(),
      reason: z.string(),
      completedAt: z.string().datetime().nullable(),
      /** The last attempt's error code when the run failed. Optional so a
       * web build newer than the API still parses. */
      failureCode: z.string().nullable().optional(),
      /** Attempts the failed run made; 0 when it did not fail. */
      attemptCount: z.number().int().nonnegative().optional(),
    })
    .nullable(),
})

export type ApiDocumentDetailResponse = z.infer<
  typeof ApiDocumentDetailResponseSchema
>

export const ApiRecordResponseSchema = z.object({
  recordId: z.string().uuid(),
  recordType: z.string(),
  recordIndex: z.number().int(),
  recordDate: z.string().nullable(),
  description: z.string().nullable(),
  amount: z.string().nullable(),
  currency: z.string().nullable(),
  confidence: z.number().nullable(),
  reviewState: z.string(),
  data: ApiJsonObjectSchema,
})

export type ApiRecordResponse = z.infer<typeof ApiRecordResponseSchema>

export const ApiRecordsResponseSchema = z.object({
  records: z.array(ApiRecordResponseSchema),
  pagination: z.object({
    limit: z.number().int(),
    offset: z.number().int(),
    returned: z.number().int(),
    total: z.number().int().nonnegative(),
  }),
})

export type ApiRecordsResponse = z.infer<typeof ApiRecordsResponseSchema>

export const ApiGroundingResponseSchema = z.object({
  groundingId: z.string().uuid(),
  extractedRecordId: z.string().uuid().nullable(),
  sourceArtifactId: z.string().uuid().nullable(),
  fieldPath: z.string().nullable(),
  providerPath: z.string().nullable(),
  pageNumber: z.number().int().nullable(),
  boundingRegions: z.array(ApiJsonObjectSchema),
  confidence: z.number().nullable(),
  createdAt: z.string().datetime(),
})

export type ApiGroundingResponse = z.infer<typeof ApiGroundingResponseSchema>

export const ApiGroundingsResponseSchema = z.object({
  groundings: z.array(ApiGroundingResponseSchema),
  pagination: z.object({
    limit: z.number().int(),
    offset: z.number().int(),
    returned: z.number().int(),
  }),
})

export type ApiGroundingsResponse = z.infer<
  typeof ApiGroundingsResponseSchema
>

export const ApiFlagResponseSchema = z.object({
  flagId: z.string().uuid(),
  extractedRecordId: z.string().uuid().nullable(),
  type: z.string(),
  severity: z.string(),
  scope: z.string(),
  status: z.string(),
  fieldPath: z.string().nullable(),
  detector: z.string(),
  message: z.string(),
  requiredForApproval: z.boolean(),
  createdAt: z.string().datetime(),
  resolvedAt: z.string().datetime().nullable(),
})

export type ApiFlagResponse = z.infer<typeof ApiFlagResponseSchema>

export const ApiFlagsResponseSchema = z.object({
  flags: z.array(ApiFlagResponseSchema),
  pagination: z.object({
    limit: z.number().int(),
    offset: z.number().int(),
    returned: z.number().int(),
    total: z.number().int().nonnegative(),
  }),
})

export type ApiFlagsResponse = z.infer<typeof ApiFlagsResponseSchema>

export const ApiNoteResponseSchema = z.object({
  noteId: z.string().uuid(),
  authorUserId: z.string(),
  // Mirrored Clerk profile of the author; null when unresolved. Optional so
  // older API deployments still parse.
  author: ApiUserSummarySchema.nullable().optional(),
  body: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type ApiNoteResponse = z.infer<typeof ApiNoteResponseSchema>

export const ApiNotesResponseSchema = z.object({
  notes: z.array(ApiNoteResponseSchema),
})

export type ApiNotesResponse = z.infer<typeof ApiNotesResponseSchema>

export const ApiCreateNoteBodySchema = z.object({
  body: z.string().trim().min(1).max(4000),
})

export type ApiCreateNoteBody = z.infer<typeof ApiCreateNoteBodySchema>

export const ApiCreateNoteResponseSchema = z.object({
  note: ApiNoteResponseSchema,
})

export type ApiCreateNoteResponse = z.infer<typeof ApiCreateNoteResponseSchema>

export const ApiActivityResponseSchema = z.object({
  events: z.array(ApiJsonObjectSchema),
  audits: z.array(ApiJsonObjectSchema),
  // Display profiles for every user id referenced by events/audits
  // (actorUserId etc.), keyed by Clerk user id. Optional so older API
  // deployments still parse.
  users: z.record(z.string(), ApiUserSummarySchema).optional(),
})

export type ApiActivityResponse = z.infer<typeof ApiActivityResponseSchema>

export const ApiArtifactAccessResponseSchema = z.object({
  artifactId: z.string().uuid(),
  accessUrl: z.string().url(),
  expiresAt: z.string().datetime(),
  headers: z.record(z.string(), z.string()),
})

export type ApiArtifactAccessResponse = z.infer<
  typeof ApiArtifactAccessResponseSchema
>

export const ApiDeleteDocumentResponseSchema = z.object({
  documentId: z.string().uuid(),
  derivedState: z.enum(documentDerivedStates),
  archivedAt: z.string().datetime(),
})

export type ApiDeleteDocumentResponse = z.infer<
  typeof ApiDeleteDocumentResponseSchema
>

// A bulk selection is always an explicit id list, capped at the list
// endpoint's own limit.
export const ApiBulkDeleteDocumentsBodySchema = z.object({
  documentIds: z.array(z.string().uuid()).min(1).max(100),
})

export type ApiBulkDeleteDocumentsBody = z.infer<
  typeof ApiBulkDeleteDocumentsBodySchema
>

export const ApiBulkDeletionResultSchema = z.object({
  documentId: z.string().uuid(),
  status: z.enum(["deleted", "skipped"]),
  // The repository error code, unwrapped. A document that was already deleted
  // comes back as skipped with document_not_found, so repeating the same
  // request is harmless.
  code: z.string().optional(),
  message: z.string().optional(),
})

export type ApiBulkDeletionResult = z.infer<typeof ApiBulkDeletionResultSchema>

export const ApiBulkDeleteDocumentsResponseSchema = z.object({
  deleted: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  results: z.array(ApiBulkDeletionResultSchema),
})

export type ApiBulkDeleteDocumentsResponse = z.infer<
  typeof ApiBulkDeleteDocumentsResponseSchema
>

export const ApiReprocessDocumentBodySchema = z
  .object({
    idempotencyKey: z.string().min(1).max(200).optional(),
    // Re-run as a different type (user correcting an auto-detected type).
    documentTypeKey: z.enum(selectableDocumentTypeKeys).optional(),
  })
  .optional()

export type ApiReprocessDocumentBody = z.infer<
  typeof ApiReprocessDocumentBodySchema
>

export const ApiReprocessDocumentResponseSchema = z.object({
  documentId: z.string().uuid(),
  processingRunId: z.string().uuid(),
  runAttemptId: z.string().uuid(),
  queueMessageId: z.string(),
  idempotentReplay: z.boolean(),
})

export type ApiReprocessDocumentResponse = z.infer<
  typeof ApiReprocessDocumentResponseSchema
>
